import * as styles from 'common/components/GenericModal/styles';
import {
  ActionButtons,
  Container,
  Content,
  Image,
} from 'common/components/GenericModal/styles';
import {
  Box, Button, Divider,
} from '@mui/material';
import {
  CreateSecondaryBannerData,
  SecondaryBanner,
  SecondaryBannerFormData,
  UpdateSecondaryBannerData,
} from 'containers/HomePageContainer/types/secondary-banner';
import { DateTimePickerField } from 'common/components/DateTimePickerField';
import {
  Form, Formik,
} from 'formik';
import { Loader } from 'common/components/Loader';
import { LoadingButton } from '@mui/lab';
import { PageType } from 'common/enums/page-type.enum';
import { PositionSelect } from 'common/components/StaticWidgetSelect';
import { SecondaryBannersConfiguration } from 'containers/HomePageContainer/components/PageConfigurator/SecondaryBannerModalContent/SecondaryBannersConfiguration';
import { SourceSelect } from 'common/components/SourceSelect';
import { TextField } from 'common/components/TextField';
import { mapSecondaryBannerFormDataToRequest } from 'containers/HomePageContainer/components/PageConfigurator/SecondaryBannerModalContent/mappers/map-banner-form-data-to-request';
import { mapWidgetDataToFormInitialValues } from 'containers/HomePageContainer/components/PageConfigurator/SecondaryBannerModalContent/mappers/map-widget-data-to-form-initial-values';
import { secondaryBannerWidgetSchema } from 'containers/HomePageContainer/components/PageConfigurator/SecondaryBannerModalContent/secondary-banner-widget.schema';
import { useCreateWidgetMutation } from 'containers/HomePageContainer/store/server/use-create-widget-mutation';
import {
  useDefaultInitialValues,
} from 'containers/HomePageContainer/components/PageConfigurator/SecondaryBannerModalContent/hooks/use-default-initial-values';
import { useSearch } from 'containers/HomePageContainer/hooks/use-search';
import { useUpdateWidgetMutation } from 'containers/HomePageContainer/store/server/use-update-widget-mutation';
import { useWidget } from 'containers/HomePageContainer/store/server/use-widget';
import CancelIcon from '@mui/icons-material/Cancel';
import React, {
  FC, useCallback,
} from 'react';
import SaveIcon from '@mui/icons-material/Save';
import SecondaryBannerImage from 'assets/widgets/thumbnails/Secondary_Banner_widget.png';

export interface SecondaryBannerModalContentProps {
  closeModal: () => void;
  id?: string;
  pageType: PageType;
}

export const SecondaryBannerModalContent: FC<SecondaryBannerModalContentProps> = ({
  closeModal,
  id,
  pageType,
}) => {
  const defaultInitialValues = useDefaultInitialValues();
  const { culture } = useSearch();
  const { data, isLoading } = useWidget<SecondaryBanner>(id);
  const createMutation = useCreateWidgetMutation(closeModal);
  const updateMutation = useUpdateWidgetMutation(closeModal);

  const handleSubmit = useCallback((values: SecondaryBannerFormData) => {
    if (id) {
      updateMutation.mutate({
        id,
        data: mapSecondaryBannerFormDataToRequest(values, pageType) as UpdateSecondaryBannerData,
      });
    } else {
      createMutation.mutate(
        mapSecondaryBannerFormDataToRequest(
          values,
          pageType,
          culture,
        ) as CreateSecondaryBannerData,
      );
    }
  }, [id, culture, createMutation, updateMutation, pageType]);

  if (pageType === PageType.Plp) {
    defaultInitialValues.position = 1;
  }
  if (id && isLoading) {
    return <Container><Loader isLoading /></Container>;
  }

  return (
    <Formik
      initialValues={data ? mapWidgetDataToFormInitialValues(data) : defaultInitialValues}
      validationSchema={secondaryBannerWidgetSchema}
      onSubmit={handleSubmit}
    >
      {({ dirty }) => (
        <Form>
          <Container>
            <Content>
              <Box display="flex">
                <Image src={SecondaryBannerImage} alt="banner widget" />
                <Box pl={3.75}>
                  <TextField name="name" label="Widget Name" fullWidth />
                  <Box display="flex">
                    <DateTimePickerField name="activeFrom" label="Valid from" sx={styles.dateTimePicker} />
                    <DateTimePickerField name="activeTo" label="Valid to" sx={styles.dateTimePicker} />
                    {pageType !== PageType.Plp && (
                      <SourceSelect name="sources" />
                    )}
                    {pageType === PageType.Plp && (
                      <PositionSelect name="position" />
                    )}
                  </Box>
                </Box>
              </Box>
              <Divider sx={styles.divider} />
              <SecondaryBannersConfiguration />
            </Content>
            <ActionButtons>
              <Button
                onClick={closeModal}
                variant="outlined"
                size="large"
                startIcon={<CancelIcon />}
              >
                Cancel
              </Button>
              <LoadingButton
                loading={createMutation.isLoading || updateMutation.isLoading}
                disabled={data && !dirty}
                variant="contained"
                type="submit"
                size="large"
                startIcon={<SaveIcon />}
              >
                Save
              </LoadingButton>
            </ActionButtons>
          </Container>
        </Form>
      )}
    </Formik>
  );
};

SecondaryBannerModalContent.defaultProps = {
  id: '',
};
